import {
    noSpaceAllowedInSchemaTest,
    themeObject,
    validationsMessages,
} from 'lib';
import { ConsentContractSvg } from '../../../assets';
import * as yup from 'yup';
import dayjs, { Dayjs } from 'dayjs';

export const JWTPropertiesErrorMessage: string = 'Invalid format';

export interface CreateConsentContractFormData {
    share: string;
    description: string;
    to: {
        id: string;
        value: string;
        header: string;
        subtitle: string;
    }[];
    jwtProperties: string;
    when: string;
    only: string[];
    start: Date | null;
    until: Date | null;
}

export type UpdateConsentContractFormData = Omit<
    CreateConsentContractFormData,
    'jwtProperties' | 'start' | 'until'
> & {
    jwtProperties: object;
    start: Dayjs | null;
    until: Dayjs | null;
};

const toFieldSchema = yup
    .array()
    .of(
        yup.object({
            id: yup.string(),
            value: yup.string(),
            header: yup.string(),
            subtitle: yup.string(),
        })
    )
    .when('share', {
        is: 'to',
        then: yup
            .array()
            .of(
                yup.object({
                    id: yup.string().required(validationsMessages.required),
                    value: yup.string().required(validationsMessages.required),
                    header: yup.string().required(validationsMessages.required),
                    subtitle: yup
                        .string()
                        .required(validationsMessages.required),
                })
            )
            .min(1, validationsMessages.required),
        otherwise: yup.array().of(
            yup.object({
                id: yup.string(),
                value: yup.string(),
                header: yup.string(),
                subtitle: yup.string(),
            })
        ),
    });

const shareAndDescriptionFieldSchema = yup.string();

export const CreateConsentContractSchema = yup.object({
    share: shareAndDescriptionFieldSchema,
    description: noSpaceAllowedInSchemaTest(
        yup.string(),
        'Enter a valid description.'
    ),
    to: toFieldSchema,
    jwtProperties: yup.string().when('share', {
        is: 'jwtProperties',
        then: yup.string().required(validationsMessages.required),
        otherwise: yup.string().notRequired(),
    }),
    when: noSpaceAllowedInSchemaTest(
        yup.string(),
        'Enter a boolean expression.'
    ),
    only: yup.array().of(yup.string()),
    start: yup
        .date()
        .nullable()
        .transform((__, originalValue) => {
            if (originalValue === null) return null;
            const parsed = dayjs(originalValue);
            return parsed.isValid() ? parsed.toDate() : new Date('Invalid');
        })
        .typeError('Enter a valid start date.')
        .min(new Date(), 'Start date cannot be in the past'),
    until: yup
        .date()
        .nullable()
        .transform((__, originalValue) => {
            if (originalValue === null) return null;
            const parsed = dayjs(originalValue);
            return parsed.isValid() ? parsed.toDate() : new Date('Invalid');
        })
        .typeError('Enter a valid end date.'),
});

export const UpdateConsentContractSchema = yup.object({
    share: shareAndDescriptionFieldSchema,
    description: noSpaceAllowedInSchemaTest(
        yup.string(),
        'Enter a valid description.'
    ),
    to: toFieldSchema,
    jwtProperties: yup.object(),
    when: noSpaceAllowedInSchemaTest(
        yup.string(),
        'Enter a boolean expression.'
    ),
    only: yup.array().of(yup.string()),
    start: yup
        .date()
        .nullable()
        .transform((__, originalValue) => {
            if (originalValue === null) return null;
            const parsed = dayjs(originalValue);
            return parsed.isValid() ? parsed.toDate() : new Date('Invalid');
        })
        .typeError('Enter a valid start date.'),
    until: yup
        .date()
        .nullable()
        .transform((__, originalValue) => {
            if (originalValue === null) return null;
            const parsed = dayjs(originalValue);
            return parsed.isValid() ? parsed.toDate() : new Date('Invalid');
        })
        .typeError('Enter a valid end date.'),
});

export const createConsentContractDefaults = {
    share: 'to',
    description: '',
    to: [],
    jwtProperties: '',
    when: '',
    only: [],
    start: null,
    until: null,
};

export const updateConsentContractDefaults = {
    ...createConsentContractDefaults,
    jwtProperties: {},
};

export const getCombinedOptionsData = (
    walletsData: any,
    userGroupsData: any,
    userEmail: string,
    userName: string
): any[] => {
    const combinedOptionsData = [
        ...(walletsData?.data?.userWallets?.map((userWallet: any) => ({
            id: userWallet.id ?? '',
            value: userWallet.fullName ?? userWallet.email ?? '',
            header: userWallet.fullName ?? '',
            subtitle: userWallet.email ?? '',
        })) ?? []),
        ...(userGroupsData?.data?.map((userGroup: any) => ({
            id: userGroup.id ?? '',
            value: userGroup.name ?? userGroup.id ?? '',
            header: userGroup.name ?? '',
            subtitle: userGroup.id ?? '',
        })) ?? []),
    ];
    const filteredOptionsData = combinedOptionsData.filter((option: any) => {
        return !(
            option.value === userEmail ||
            option.subtitle === userEmail ||
            option.value === userName ||
            option.header === userName
        );
    });
    filteredOptionsData.sort((a: any, b: any) => {
        return a.header.toLowerCase().localeCompare(b.header.toLowerCase());
    });
    return filteredOptionsData;
};

export const radioButtonData: {
    label: string;
    value: string;
}[] = [
    {
        label: 'To User(s)',
        value: 'to',
    },
    {
        label: 'JWT Properties',
        value: 'jwtProperties',
    },
];

export const consentContractConfig: {
    [key: string]: {
        imgSrc: string;
        headingText?: string;
        iconStyles?: React.CSSProperties;
    };
} = {
    'consent-contracts': {
        imgSrc: ConsentContractSvg,
        headingText: 'New Consent Contract',
        iconStyles: {
            width: '70px',
            height: '72.42px',
        },
    },
};

export const consentContractStyles = {
    radioButtonGroupContainer: {
        mt: 2.5,
    },
    gridContainer: {
        mt: 2,
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    fieldStyles: {
        width: '100%',
        '& .MuiFormControl-root': {
            width: '100%',
        },
    },
    dateAndTimePickerContainer: {
        mt: 2,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'start',
        gap: 2,
    },
    dateAndTimePickerStyles: {
        '& .MuiFormControl-root': {
            width: '225px',
        },
    },
    dividerContainerStyles: {
        display: 'flex',
        justifyContent: 'center',
        fontFamily: 'Red Hat Text',
        fontSize: '40px',
        fontWeight: 300,
        lineHeight: '52.92px',
        color: themeObject.palette.grayScale[500],
    },
    jwtProperties: {
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        borderRadius: '2px',
        boxShadow: 'none',
    },
    jwtPropertiesError: {
        border: `1px solid ${themeObject.palette.tertiary.red[300]}`,
        borderRadius: '2px',
        boxShadow: 'none',
    },
    jwtPropertiesErrorHeight: '216px',
    jwtPropertiesErrorContainer: {
        paddingTop: '2px',
    },
    updateContainerStyles: {
        p: '32px 31.5px 32px 45.5px',
        backgroundColor: themeObject.palette.background.default,
        border: `1px solid ${themeObject.palette.grayScale[300]}`,
        width: '100%',
        borderRadius: '10px',
        mt: '15px',
    },
    shareLabelStyles: {
        ...themeObject.typography.inputLabel,
        lineHeight: '21.17px',
        letterSpacing: '0.15px',
        mb: '12px',
        color: themeObject.palette.grayScale[800],
    },
    shareValueStyles: {
        ...themeObject.typography.Components.inputText,
        lineHeight: '24px',
        ml: '12px',
        color: themeObject.palette.grayScale[800],
    },
    expireButtonContainer: {
        display: 'flex',
        mb: '4px',
    },
};
